import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function CookiesPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="cookies-page">
      <Helmet>
        <title>Politique de Cookies | École de Musique Pour Tous - Périgueux</title>
        <meta name="description" content="Politique de cookies de l'École de Musique Pour Tous à Périgueux. Informations sur l'utilisation des cookies et vos choix." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://ecoledemusiqueperigueux.fr/cookies" />
      </Helmet>
      <section className="py-16 bg-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-dancing text-5xl text-burgundy mb-8">
              Politique de cookies
            </h1>

            <div className="bg-white rounded-3xl shadow-sm p-8 space-y-8 font-nunito">
              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">1. Qu'est-ce qu'un cookie ?</h2>
                <p className="text-gray-700 leading-relaxed">
                  Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, tablette, 
                  smartphone) lorsque vous visitez un site web. Les cookies permettent au site de 
                  reconnaître votre appareil et de mémoriser certaines informations sur votre visite.
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">2. Les cookies que nous utilisons</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Notre site utilise uniquement des <strong>cookies essentiels</strong> au bon fonctionnement 
                  du site. Nous n'utilisons aucun cookie de tracking publicitaire ni de statistiques externes.
                </p>
                
                <div className="bg-cream rounded-xl p-4 space-y-4">
                  <div>
                    <h4 className="font-bold text-gray-800">Cookie de consentement</h4>
                    <p className="text-gray-600 text-sm">
                      <strong>Nom :</strong> cookie_consent<br />
                      <strong>Durée :</strong> 1 an<br />
                      <strong>Objectif :</strong> Mémorise votre choix concernant l'acceptation des cookies
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800">Cookie de session</h4>
                    <p className="text-gray-600 text-sm">
                      <strong>Nom :</strong> session_id<br />
                      <strong>Durée :</strong> Session de navigation<br />
                      <strong>Objectif :</strong> Assure le bon fonctionnement technique du site
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">3. Cookies tiers</h2>
                <p className="text-gray-700 leading-relaxed">
                  Notre site intègre une carte Google Maps sur la page Contact. Google peut déposer des 
                  cookies lors de l'affichage de cette carte. Pour plus d'informations sur la politique 
                  de confidentialité de Google, consultez :{' '}
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-burgundy hover:underline"
                  >
                    https://policies.google.com/privacy
                  </a>
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">4. Comment gérer les cookies ?</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Vous pouvez à tout moment modifier vos préférences en matière de cookies :
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>
                    <strong>Via votre navigateur :</strong> Vous pouvez configurer votre navigateur pour 
                    refuser tous les cookies ou pour vous avertir lorsqu'un cookie est envoyé.
                  </li>
                  <li>
                    <strong>Suppression des cookies :</strong> Vous pouvez supprimer les cookies déjà 
                    stockés sur votre appareil via les paramètres de votre navigateur.
                  </li>
                </ul>
                
                <div className="bg-coral/10 rounded-xl p-4 mt-4">
                  <p className="text-gray-700 text-sm">
                    <strong>Note :</strong> Le refus des cookies essentiels peut empêcher le bon 
                    fonctionnement de certaines fonctionnalités du site.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">5. Liens vers les paramètres des navigateurs</h2>
                <ul className="space-y-2 text-gray-700">
                  <li>
                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-burgundy hover:underline">
                      Google Chrome
                    </a>
                  </li>
                  <li>
                    <a href="https://support.mozilla.org/fr/kb/protection-renforcee-contre-pistage-firefox-ordinateur" target="_blank" rel="noopener noreferrer" className="text-burgundy hover:underline">
                      Mozilla Firefox
                    </a>
                  </li>
                  <li>
                    <a href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-burgundy hover:underline">
                      Safari
                    </a>
                  </li>
                  <li>
                    <a href="https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-burgundy hover:underline">
                      Microsoft Edge
                    </a>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">6. Vos droits</h2>
                <p className="text-gray-700 leading-relaxed">
                  Pour plus d'informations sur vos droits concernant vos données personnelles, consultez 
                  notre <Link to="/confidentialite" className="text-burgundy hover:underline">politique de confidentialité</Link>.
                </p>
              </section>

              <section>
                <h2 className="font-bold text-xl text-burgundy mb-4">7. Contact</h2>
                <p className="text-gray-700 leading-relaxed">
                  Pour toute question concernant notre utilisation des cookies :<br /><br />
                  <strong>École de Musique Pour Tous</strong><br />
                  Email : <a href="mailto:musiquepourtous24@gmail.com" className="text-burgundy hover:underline">musiquepourtous24@gmail.com</a>
                </p>
              </section>

              <p className="text-gray-500 text-sm pt-4 border-t">
                Dernière mise à jour : Janvier 2026
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
